import { CSSProperties, FC, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { observer } from 'mobx-react-lite';
import { useStore } from 'hooks/useRootStore';
import { updateImageUrl } from 'utils/convertString';
import { useWindowSize } from 'hooks/useWindowSize';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { ControlledInput } from 'components/Inputs/ControlledInput';
import { ImageUploader } from 'components/Core/ImageUploader/ImageUploader';
import { TableActionsButtons } from 'components/RequestsComponents/TableActionsButtons';
import { DatePickerComponent } from 'components/Inputs/DatePickerComponents/DatePickerComponent';
import { InputCheckbox } from 'components/Inputs/InputCheckbox';
import RequestHeader from 'components/RequestsComponents/RequestHeader';
import { IMAGE_HEIGHT, IMAGE_WIDTH } from 'components/BrandComponents/BrandCataloguesPage/constants';
import { catalogueRequestFormTranslations } from './translations';
import { CatalogueRequestFormTransFields, ICatalogueRequestForm } from './types';
import styles from './catalogueRequestForm.module.scss';

const buttonStyleProp: CSSProperties = {
  width: '100%',
};

export const CatalogueRequestForm: FC = observer(() => {
  const {
    requestsStore: { catalogueRequestData },
  } = useStore();

  const { t } = useMyTranslations<CatalogueRequestFormTransFields>([catalogueRequestFormTranslations.name]);

  const windowSize = useWindowSize();

  const defaultValues = useMemo((): ICatalogueRequestForm | undefined => {
    if (!catalogueRequestData) return undefined;
    const savedData = localStorage.getItem(catalogueRequestData.id);
    return savedData
      ? JSON.parse(savedData)
      : {
          ...catalogueRequestData,
          checkedPages: catalogueRequestData?.pages.map(() => false) || [],
        };
  }, [catalogueRequestData]);

  const useFormMethods = useForm<ICatalogueRequestForm>({
    defaultValues,
  });

  const { watch, getValues } = useFormMethods;

  useEffect(() => {
    const form = getValues();
    if (form.id) {
      localStorage.setItem(form.id, JSON.stringify(form));
    }
  }, [watch()]);

  return catalogueRequestData ? (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <FormProvider {...(useFormMethods as any)}>
      <RequestHeader>
        <TableActionsButtons
          id={catalogueRequestData.id}
          entity='catalogues'
          status={catalogueRequestData.status}
          isApproveDisabled={watch('checkedPages').filter((item) => item).length !== catalogueRequestData.pages.length}
        />
      </RequestHeader>
      <div className={styles.content}>
        <form className={styles.form}>
          <section className={`${styles.leftSection}`}>
            <div className={`${styles.formSection}`}>
              <h6 className={`${styles.title} ${styles.titleWithMargin}`}>{t('title')}</h6>
              <ControlledInput
                name='name'
                type='text'
                label={t('name')}
                disabled
                classNameProp={catalogueRequestData.changedFieldsNames.includes('name') ? styles.highlight : undefined}
              />
              <DatePickerComponent
                label={t('startDate')}
                name='startDate'
                buttonStyleProp={buttonStyleProp}
                stylesProp={{ marginBottom: 24 }}
                disabled
              />
              <DatePickerComponent
                label={t('expiredDate')}
                name='expiredDate'
                buttonStyleProp={buttonStyleProp}
                stylesProp={{ marginBottom: 24 }}
                disabled
              />
            </div>
          </section>
          <section className={styles.rightSection}>
            <ImageUploader
              name='previewUrl'
              title={t('previewUrl')}
              path='catalogues'
              disabled
              classNameProp={
                catalogueRequestData.changedFieldsNames.includes('previewUrl') ? styles.highlight : undefined
              }
            />
          </section>
        </form>
        <div className={styles.pages}>
          {catalogueRequestData.pages.map((page, index) => {
            return (
              <div key={page.id} className={styles.page}>
                <img
                  className={`${styles.image} ${
                    catalogueRequestData.changedFieldsNames.includes('pages') &&
                    catalogueRequestData.changedPagesPositions.includes(page.position)
                      ? styles['need-to-check']
                      : ''
                  }`}
                  src={updateImageUrl(
                    page.imageUrl,
                    windowSize?.wWidth || IMAGE_WIDTH,
                    windowSize?.wHeight || IMAGE_HEIGHT
                  )}
                  loading='lazy'
                />
                <div className={styles.pageRow}>
                  <span className={styles.count}>
                    {page.position}/{catalogueRequestData.pages.length}
                  </span>
                  <InputCheckbox
                    name={`checkedPages.${index}`}
                    isRemoveMargin
                    labelText={t('Approve page')}
                    checkBoxSize='medium'
                    checkBoxPosition='right'
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </FormProvider>
  ) : (
    <p>{t('no data')}</p>
  );
});
